import { useState, useEffect, useCallback } from 'react';
import { TrainingSessionItem, ActivityType, SavedWord } from '../types/vocabulary';
import { getDailyChallenge, getThemeSession, updateWordStats, updateUserStatsAfterActivity, updateSavedWord, incrementCompletedSessions, markDailyTrainingCompleted, resetDailyTraining, saveTrainingProgress, loadTrainingProgress, clearTrainingProgress } from '../services/vocabularyService';
import { logUserActivity } from '../services/activityLogService';

export interface ActivityResult {
  wordId: string;
  word: string;
  activityType: ActivityType;
  success: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  timestamp: number;
}

export function useVocabularyTraining(userId: string | undefined, user?: any, themeId?: string) {
  const [session, setSession] = useState<TrainingSessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [results, setResults] = useState<ActivityResult[]>([]);

  useEffect(() => {
    if (!userId) {
        setLoading(false);
        return;
    }
    setLoading(true);

    if (themeId) {
      // Theme Mode
      getThemeSession(userId, themeId).then(items => {
        setSession(items);
        setLoading(false);
      });
    } else {
      // Daily Mode
      getDailyChallenge(userId).then(items => {
        setSession(items);

        // Load saved progress for today (only for daily challenge)
        const savedProgress = loadTrainingProgress(userId);
        if (savedProgress) {
          // Validate that the saved progress is still valid
          const { currentWordIndex, currentActivityIndex } = savedProgress;
          if (currentWordIndex < items.length) {
            const wordItem = items[currentWordIndex];
            if (wordItem && currentActivityIndex < wordItem.activities.length) {
              setCurrentWordIndex(currentWordIndex);
              setCurrentActivityIndex(currentActivityIndex);
            }
          }
        }

        setLoading(false);
      });
    }
  }, [userId, themeId]);

  // Ensure indices are within bounds - fix for step 8 issue
  useEffect(() => {
    if (!loading && session.length > 0) {
      // Validate and fix currentWordIndex
      if (currentWordIndex >= session.length) {
        setCompleted(true);
        if (userId) {
          markDailyTrainingCompleted(userId);
          clearTrainingProgress(userId);
        }
        return;
      }
      
      const item = session[currentWordIndex];
      if (item && currentActivityIndex >= item.activities.length) {
        // If we've gone past activities for this word, move to next word
        if (currentWordIndex < session.length - 1) {
          setCurrentWordIndex(currentWordIndex + 1);
          setCurrentActivityIndex(0);
          if (userId) saveTrainingProgress(userId, currentWordIndex + 1, 0);
        } else {
          setCompleted(true);
          if (userId) {
            markDailyTrainingCompleted(userId);
            clearTrainingProgress(userId);
          }
        }
      }
    }
  }, [loading, session, currentWordIndex, currentActivityIndex, userId]);

  const currentItem = session[currentWordIndex];
  const currentActivity = currentItem?.activities[currentActivityIndex];

  const handleActivityComplete = useCallback(async (success: boolean, userAnswer?: string, correctAnswer?: string) => {
    if (!userId || !currentItem) return;

    // Track result
    const result: ActivityResult = {
      wordId: currentItem.word.id,
      word: currentItem.word.word,
      activityType: currentActivity!,
      success,
      userAnswer,
      correctAnswer,
      timestamp: Date.now()
    };
    setResults(prev => [...prev, result]);

    // Update Stats (Background)
    updateWordStats(userId, currentItem.word.id, { success }).catch(console.error);
    updateUserStatsAfterActivity(
      userId, 
      { success, date: new Date().toISOString().split('T')[0] }, 
      user,
      { activityType: currentActivity!, word: currentItem.word.word }
    ).catch(console.error);

    // Move to next
    let nextWordIndex = currentWordIndex;
    let nextActivityIndex = currentActivityIndex;
    
    if (currentActivityIndex < currentItem.activities.length - 1) {
      nextActivityIndex = currentActivityIndex + 1;
      setCurrentActivityIndex(nextActivityIndex);
    } else {
      // Next Word
      if (currentWordIndex < session.length - 1) {
        nextWordIndex = currentWordIndex + 1;
        nextActivityIndex = 0;
        setCurrentWordIndex(nextWordIndex);
        setCurrentActivityIndex(nextActivityIndex);
      } else {
        setCompleted(true);
        if (userId) {
          incrementCompletedSessions(userId).catch(console.error);
          
          // Log completion as a single event for Activity Feeds
          const totalXP = results.filter(r => r.success).length * 10 + results.filter(r => !r.success).length * 1;
          logUserActivity(
             userId, 
             user?.displayName || user?.username || 'User', 
             'training', 
             totalXP, 
             themeId ? 'Theme Training' : 'Daily Training'
          ).catch(console.error);

          // Theme training also counts as Daily Training for streak purposes
          markDailyTrainingCompleted(userId);
          if (!themeId) {
             clearTrainingProgress(userId);
          }
        }
        return;
      }
    }
    
    // Save progress after moving to next step
    if (!themeId) {
        saveTrainingProgress(userId, nextWordIndex, nextActivityIndex);
    }
  }, [userId, currentItem, currentActivityIndex, currentWordIndex, session.length, currentActivity, themeId]);

  const restartTraining = useCallback(() => {
    if (!userId) return;
    if (!themeId) {
        resetDailyTraining(userId);
        clearTrainingProgress(userId);
    }
    setCompleted(false);
    setCurrentWordIndex(0);
    setCurrentActivityIndex(0);
    setResults([]);
    setLoading(true);

    if (themeId) {
         getThemeSession(userId, themeId).then(items => {
            setSession(items);
            setLoading(false);
         });
    } else {
        getDailyChallenge(userId).then(items => {
            setSession(items);
            setLoading(false);
        });
    }
  }, [userId, themeId]);

  const handleUpdateWord = useCallback(async (updates: Partial<SavedWord>) => {
    if (!userId || !currentItem) return;
    await updateSavedWord(userId, currentItem.word.id, updates);

    // Optimistically update local session state
    setSession(prev => {
        const newSession = [...prev];
        const newWord = { ...newSession[currentWordIndex].word, ...updates };
        newSession[currentWordIndex] = { ...newSession[currentWordIndex], word: newWord };
        return newSession;
    });
  }, [userId, currentItem, currentWordIndex]);

  const totalSteps = session.reduce((acc, item) => acc + item.activities.length, 0);
  const currentStep = session.slice(0, currentWordIndex).reduce((acc, item) => acc + item.activities.length, 0) + currentActivityIndex;

  return {
    loading,
    session,
    currentWord: currentItem?.word,
    currentActivity,
    progress: {
      total: totalSteps,
      current: currentStep + 1, // Show 1-based step number (step 1, 2, etc.) - currentStep is 0-indexed
      percent: totalSteps === 0 ? 0 : Math.round(((currentStep + 1) / totalSteps) * 100)
    },
    handleActivityComplete,
    handleUpdateWord,
    completed,
    restartTraining,
    results
  };
}
